import { useQuery } from '@apollo/react-hooks';
import { UAContext } from '@quentin-sommer/react-useragent';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { $enum } from 'ts-enum-util';
import { BirthdayPicker, Checkbox, FocusError, Input, PhoneInput, Select } from '~components/form';
import { useThemeContext } from '~components/theme-provider';
import Typography from '~components/typography';
import {
  CustomFieldModelTypeOptions,
  RelationshipOptions,
} from '~graphql/__generated__/globalTypes';
import {
  SP_QueryCities,
  SP_QueryCities_getCities_cities,
  SP_QueryCitiesVariables,
} from '~graphql/__generated__/SP_QueryCities';
import {
  SP_QueryCountries,
  SP_QueryCountriesVariables,
} from '~graphql/__generated__/SP_QueryCountries';
import { QUERY_CITIES, QUERY_COUNTRIES } from '~graphql/person-detail';
import { useTranslation } from '~i18n';
import { usePersonDropdownOptions } from '~utils/use-person-dropdown-options';

interface Value {
  label: string;
  value: string | null;
}
interface IEmergencyFormProps {
  values: any;
  deduplicatePhoneCodes: Array<Value>;
  setFieldValue: (name: string, value: any) => void;
}
const EmergencyForm: React.FC<IEmergencyFormProps> = ({
  deduplicatePhoneCodes,
  values,
  setFieldValue,
}) => {
  const { t } = useTranslation(['student_portal']);
  const [defaultCities, setDefaultCities] = useState<Array<Value>>([]);
  const uaResults = useContext(UAContext).uaResults as UAResults;
  const isMobile = uaResults.mobile;
  const { ORG, topLandlordId } = useThemeContext();
  const { titleOptions } = usePersonDropdownOptions({
    variables: {
      topLandlordId,
      model: CustomFieldModelTypeOptions.EMERGENCY_CONTACT,
    },
  });

  const { refetch: refetchCity } = useQuery<SP_QueryCities, SP_QueryCitiesVariables>(QUERY_CITIES, {
    fetchPolicy: 'network-only',
    skip: true,
  });
  const { data: countryData } = useQuery<SP_QueryCountries, SP_QueryCountriesVariables>(
    QUERY_COUNTRIES,
    {
      variables: {
        nameLike: '',
      },
      fetchPolicy: 'cache-and-network',
    }
  );
  const formedCountries = countryData?.getCountries?.countries?.map(countryItem => {
    if (countryItem) {
      return {
        label: countryItem?.name,
        value: countryItem?.id,
      };
    } else {
      return null;
    }
  });
  const defalselyCountries = _.compact(formedCountries);

  const formedCityOrUniData = (cities: Array<SP_QueryCities_getCities_cities | null>) => {
    return cities?.map(cityItem => {
      if (cityItem) {
        return {
          label: cityItem?.name,
          value: cityItem?.id,
        };
      } else {
        return null;
      }
    });
  };

  const relationshipOptions = $enum(RelationshipOptions)
    .map(item => {
      return {
        label: t(`form.options.relation.${item.toLowerCase()}`),
        value: item,
      };
    })
    .sort((current, next) => {
      if (current.value === RelationshipOptions.OTHER) {
        return 1;
      }
      if (next.value === RelationshipOptions.OTHER) {
        return -1;
      }
      return 0;
    });
  useEffect(() => {
    if (values.countryTerritoryId) {
      refetchCity({
        countryId: values.countryTerritoryId.value,
        nameLike: '',
      }).then(({ data: cityData }) => {
        if (cityData?.getCities?.cities) {
          const defalselyCityData = _.compact(formedCityOrUniData(cityData.getCities.cities));
          setDefaultCities(defalselyCityData);
        } else {
        }
      });
    }
  }, [refetchCity, setFieldValue, values.countryTerritoryId]);
  return (
    <div className="emergency-form">
      <Select
        labelClassName="emergency-form__input-label"
        selectType="custom"
        placeholder={t('form.field.title_placeholder')}
        options={titleOptions}
        className="emergency-form__input"
        menuShouldScrollIntoView={false}
        label={t('form.field.title')}
        name="title"
      />
      <Input
        className="emergency-form__input"
        label={t('form.field.first_name')}
        placeholder={t('form.field.first_name')}
        name="firstName"
      />
      <Input
        className="emergency-form__input"
        label={`${t('form.field.middle_name')} (${t('form.field.optional')})`}
        placeholder={t('form.field.middle_name')}
        name="middleName"
      />
      <Input
        className="emergency-form__input"
        label={t('form.field.last_name')}
        placeholder={t('form.field.last_name')}
        name="lastName"
      />
      <Input
        className="emergency-form__input"
        label={t('form.field.email_address')}
        placeholder={t('form.field.email')}
        name="contactEmail"
      />
      <PhoneInput
        options={deduplicatePhoneCodes}
        label={t('form.field.phone')}
        placeholder={t('form.field.phone')}
        name="mobilePhoneNumber"
        codeName="mobilePhoneAreaCode"
        className="emergency-form__input"
      />
      <PhoneInput
        options={deduplicatePhoneCodes}
        label={`${t('form.field.home_phone')} (${t('form.field.optional')})`}
        name="homePhoneNumber"
        codeName="homePhoneAreaCode"
        placeholder={t('form.field.home_phone')}
        className="emergency-form__input"
      />
      <BirthdayPicker
        className="emergency-form__input"
        label={t('form.field.birthday')}
        name="dateOfBirth"
        placeholder={t('form.filed.date_picker_placeholder')}
      />
      <Input
        className="emergency-form__input"
        label={t('form.field.address_line_1')}
        placeholder={t('form.field.address_line1_placeholder')}
        name="addressLine1"
      />
      <Input
        className="emergency-form__input"
        label={`${t('form.field.address_line_2')} (${t('form.field.optional')})`}
        placeholder={t('form.field.address_line2_placeholder')}
        name="addressLine2"
      />
      <Select
        selectType="custom"
        options={defalselyCountries}
        className="emergency-form__input"
        label={t('form.field.country')}
        menuShouldScrollIntoView={false}
        placeholder={t('form.field.country')}
        name="countryTerritoryId"
      />
      <Select
        name="cityTownId"
        selectType="asyncCreatable"
        className="emergency-form__input"
        label={t('form.field.city')}
        menuShouldScrollIntoView={false}
        placeholder={t('form.field.city')}
        defaultOptions={defaultCities}
        loadOptions={(inputValue: string) => {
          return new Promise(async resolve => {
            const { data: cityData } = await refetchCity({
              countryId: values.countryTerritoryId.value,
              nameLike: inputValue,
            });
            if (cityData?.getCities?.cities) {
              const defalselyCityData = _.compact(formedCityOrUniData(cityData.getCities.cities));
              resolve(defalselyCityData);
            } else {
              resolve([]);
            }
          });
        }}
        isDisabled={_.isEmpty(values.countryTerritoryId)}
        noOptionsMessage={() => {
          return t('form.select.add_city');
        }}
        formatCreateLabel={(inputValue: string) => {
          return (
            <Typography cursor="pointer" variant="body3" color="var(--color-grey_99)">
              {t('form.select.click_add_city')}{' '}
              <Typography variant="body3" textDecoration="underline">
                {t('form.select.click_add_city.add', { city: inputValue })}
              </Typography>
            </Typography>
          );
        }}
      />
      <Input
        placeholder={t('form.field.postcode_placeholder')}
        name="postcode"
        className="emergency-form__input"
        label={`${t('form.field.postcode')} (${t('form.field.optional')})`}
      />
      <Select
        selectType="custom"
        labelClassName="emergency-form__input-label"
        placeholder={t(`form.placeholder.guarantor_tenantRelation.${ORG}`)}
        name="relationship"
        options={relationshipOptions}
        menuShouldScrollIntoView={false}
        className="emergency-form__input"
        label={t(`form.field.tenant_relation.${ORG}`)}
      />
      <Checkbox
        name="contactWithoutPermission"
        className="personal-details__checkbox"
        labelClassName="create-account-label"
        label={t('check_in.emergency_contact.agreement')}
      />
      <FocusError />
      <style jsx>
        {`
          .emergency-form {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
          }
          :global(.emergency-form__input) {
            width: ${isMobile ? '100%' : '360px'};
            margin-bottom: 16px;
          }
        `}
      </style>
    </div>
  );
};

export default EmergencyForm;
